import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Country, State } from 'country-state-city';
import { useLocation, useNavigate } from 'react-router-dom';
import { WalletContext } from '../context/WalletContex';
import TopBar from '../components/layout/TopBar';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import OtpModal from '../components/ui/OtpModal';
import PasswordInput from '../components/ui/PasswordInput';
import { isPasswordValid } from '@ikhua/shared';
import apiClient from '../utils/apiClient';
import { toast } from 'react-toastify';
import {
  HiShieldCheck, HiArrowRightOnRectangle, HiDocumentCheck,
  HiIdentification, HiCamera, HiCheckCircle, HiClock,
  HiXCircle, HiChevronRight, HiEnvelope, HiCheckBadge, HiLockClosed, HiDocumentText, HiArrowPath,
  HiBuildingLibrary, HiDevicePhoneMobile, HiPlus, HiTrash, HiPhone, HiAtSymbol,
} from 'react-icons/hi2';

const CURRENCIES = [
  'AED','ARS','AUD','BDT','BHD','BRL','CAD','CHF','CLP','CNY',
  'COP','CZK','DKK','EGP','EUR','GBP','GHS','HKD','HUF','IDR',
  'ILS','INR','IQD','JPY','KES','KRW','KWD','LBP','MAD','MXN',
  'MYR','NGN','NOK','NZD','OMR','PEN','PHP','PKR','PLN','QAR',
  'RON','RUB','SAR','SEK','SGD','THB','TRY','TWD','TZS','UAH',
  'UGX','USD','VND','XAF','XOF','ZAR','ZMW',
];

export default function Profile() {
  const location = useLocation();
  const navigate = useNavigate();
  const { wallet, backupWallet, skipBackup, signOutWallet, needsBackup, syncRemoteState } = useContext(WalletContext);

  const [backupOpen, setBackupOpen]         = useState(false);
  const [verifyChecking, setVerifyChecking] = useState(false);
  const [kycOpen, setKycOpen]               = useState(false);
  const [signOutOpen, setSignOutOpen]       = useState(false);
  const [changePwOpen, setChangePwOpen]     = useState(false);
  const [payAccounts, setPayAccounts]       = useState([]);
  const [payAccOpen, setPayAccOpen]         = useState(false);
  const [deleteAcc, setDeleteAcc]           = useState(null);
  const [deleteLoading, setDeleteLoading]   = useState(false);
  const [phoneOpen, setPhoneOpen]           = useState(false);

  /* Username state */
  const [uName, setUName]             = useState('');
  const [uNameCheck, setUNameCheck]   = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid'
  const [uNameSaving, setUNameSaving] = useState(false);
  const [uNameSuccess, setUNameSuccess] = useState(false);

  /* Auto-open modals when navigated with state */
  useEffect(() => {
    if (location.state?.openBackup) setBackupOpen(true);
    if (location.state?.section === 'kyc') setKycOpen(true);
    if (location.state?.openPayAcc) setPayAccOpen(true);
  }, [location.state]);

  useEffect(() => {
    if (!wallet?.email) return;
    apiClient.get(`/payment-accounts?email=${encodeURIComponent(wallet.email)}`)
      .then(r => setPayAccounts(r.data.accounts || []))
      .catch(() => {});
  }, [wallet?.email]);

  /* Debounced username availability check */
  useEffect(() => {
    const q = uName.trim();
    if (!q || q.length < 3) { setUNameCheck(null); return; }
    if (q === wallet?.username) { setUNameCheck(null); return; }
    setUNameCheck('checking');
    const t = setTimeout(async () => {
      try {
        const r = await apiClient.get(`/wallet/check-username?username=${encodeURIComponent(q)}`);
        setUNameCheck(r.data.available ? 'available' : 'taken');
      } catch (e) {
        setUNameCheck(e.response?.data?.error?.includes('format') ? 'invalid' : 'taken');
      }
    }, 500);
    return () => clearTimeout(t);
  }, [uName, wallet?.username]);

  const saveUsername = async () => {
    if (!uName.trim() || uNameCheck !== 'available') return;
    setUNameSaving(true);
    try {
      await apiClient.patch('/wallet/set-username', { email: wallet.email, username: uName.trim() });
      setUNameSuccess(true);
      await syncRemoteState(wallet);
      toast.success(`@${uName.trim()} is now your username`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not save username');
    } finally {
      setUNameSaving(false);
    }
  };

  const kycStatus = wallet?.kycStatus;
  const isUnverified = !kycStatus || kycStatus === 'none';

  const kycBadge = isUnverified
    ? { label: 'Not Verified', color: '#4B5170', bg: 'rgba(75,81,112,0.12)' }
    : kycStatus === 'pending'
    ? { label: 'Under Review', color: '#F59E0B', bg: 'rgba(245,158,11,0.1)' }
    : kycStatus === 'approved'
    ? { label: 'Verified',     color: '#10B981', bg: 'rgba(16,185,129,0.12)' }
    : { label: 'Rejected',     color: '#EF4444', bg: 'rgba(239,68,68,0.1)'  };

  const kycRowSub = isUnverified
    ? 'Verify identity to unlock P2P trading'
    : kycStatus === 'pending'
    ? 'Documents under review — 24–48 hours'
    : kycStatus === 'approved'
    ? 'Identity verified — P2P trading unlocked'
    : (wallet?.kycRejectionReason ? `Rejected: ${wallet.kycRejectionReason}` : 'Rejected — tap to resubmit');

  return (
    <div>
      <TopBar title="Profile" />

      {/* Avatar + user ID */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 20px 28px', gap: 10 }}>
        <div style={{ width: 72, height: 72, borderRadius: 24, background: 'rgba(124,92,246,0.15)', border: '2px solid rgba(124,92,246,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px' }}>
          👤
        </div>
        {wallet?.email
          ? <span style={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>{wallet.email}</span>
          : <span style={{ fontSize: '14px', color: '#4B5170' }}>Guest Wallet</span>
        }
        {isUnverified ? (
          <span style={{ fontSize: '12px', color: '#4B5170' }}>Identity Not Verified</span>
        ) : kycStatus === 'approved' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 14px', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 20 }}>
            <HiCheckBadge size={16} style={{ color: '#10B981' }} />
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#10B981', letterSpacing: '0.01em' }}>Verified</span>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', background: kycBadge.bg, borderRadius: 20 }}>
            {kycStatus === 'pending'
              ? <HiClock   size={13} style={{ color: kycBadge.color }} />
              : <HiXCircle size={13} style={{ color: kycBadge.color }} />}
            <span style={{ fontSize: '12px', fontWeight: 600, color: kycBadge.color }}>{kycBadge.label}</span>
          </div>
        )}
      </div>

      {/* Wallet section */}
      <div className="section-title">Wallet</div>
      <div style={{ margin: '0 16px 8px', background: '#110E30', borderRadius: 20, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden' }}>
        {needsBackup && (
          <ProfileRow
            icon={<HiShieldCheck size={20} style={{ color: '#7C5CF6' }} />}
            iconBg="rgba(124,92,246,0.12)"
            label="Create Account"
            sub="Access your wallet from any device"
            onClick={() => setBackupOpen(true)}
          />
        )}
        {!needsBackup && (
          <ProfileRow
            icon={<HiShieldCheck size={20} style={{ color: '#10B981' }} />}
            iconBg="rgba(16,185,129,0.12)"
            label="Account Active"
            sub={`Signed in as ${wallet?.email}`}
          />
        )}
      </div>

      {/* Email verification nudge */}
      {wallet?.backedUp && !wallet?.emailVerified && (
        <div style={{ margin: '12px 16px 0', background: 'rgba(124,92,246,0.08)', border: '1px solid rgba(124,92,246,0.2)', borderRadius: 16, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <HiEnvelope size={20} style={{ color: '#7C5CF6', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginBottom: 2 }}>Email not verified</div>
            <div style={{ fontSize: '12px', color: '#4B5170', lineHeight: 1.5 }}>Check your inbox and click the verification link.</div>
          </div>
          <button
            onClick={async () => {
              setVerifyChecking(true);
              await syncRemoteState(wallet);
              setVerifyChecking(false);
              if (!wallet?.emailVerified) toast.info('Not verified yet — check your inbox');
            }}
            style={{ background: 'rgba(124,92,246,0.15)', border: '1px solid rgba(124,92,246,0.3)', borderRadius: 10, padding: '7px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
          >
            <HiArrowPath size={14} style={{ color: '#7C5CF6', animation: verifyChecking ? 'spin 1s linear infinite' : 'none' }} />
            <span style={{ fontSize: '12px', color: '#7C5CF6', fontWeight: 700 }}>{verifyChecking ? 'Checking…' : 'Refresh'}</span>
          </button>
        </div>
      )}

      {/* Security / KYC */}
      <div className="section-title" style={{ marginTop: 16 }}>Security & Verification</div>
      <div style={{ margin: '0 16px 8px', background: '#110E30', borderRadius: 20, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden' }}>
        <ProfileRow
          icon={<HiIdentification size={20} style={{ color: '#7C5CF6' }} />}
          iconBg="rgba(124,92,246,0.12)"
          label="Identity Verification (KYC)"
          sub={kycRowSub}
          onClick={kycStatus !== 'approved' ? () => setKycOpen(true) : undefined}
        />
        {kycStatus === 'rejected' && wallet?.kycRejectionReason && (
          <div style={{ margin: '0 14px 10px', padding: '9px 12px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.18)', borderRadius: 12 }}>
            <span style={{ fontSize: '12px', color: '#EF4444', lineHeight: 1.5, display: 'block' }}>
              <strong>Reason:</strong> {wallet.kycRejectionReason}
            </span>
          </div>
        )}
        <div className="divider" style={{ margin: '0 16px' }} />
        <ProfileRow
          icon={<HiDocumentCheck size={20} style={{ color: '#6274F1' }} />}
          iconBg="rgba(98,116,241,0.12)"
          label="P2P Trading"
          sub={kycStatus === 'approved' ? 'Unlocked' : 'Requires KYC verification'}
          onClick={kycStatus !== 'approved' ? () => setKycOpen(true) : undefined}
        />
        {wallet?.backedUp && (
          <>
            <div className="divider" style={{ margin: '0 16px' }} />
            <ProfileRow
              icon={<HiLockClosed size={20} style={{ color: '#F59E0B' }} />}
              iconBg="rgba(245,158,11,0.1)"
              label="Change Password"
              sub="Update your backup password"
              onClick={() => setChangePwOpen(true)}
            />
            <div className="divider" style={{ margin: '0 16px' }} />
            <ProfileRow
              icon={<HiPhone size={20} style={{ color: '#10B981' }} />}
              iconBg="rgba(16,185,129,0.1)"
              label="Phone Number"
              sub={wallet?.phone || 'Not set — complete KYC to add your number'}
              onClick={() => setPhoneOpen(true)}
            />
          </>
        )}
      </div>

      {/* Username */}
      {wallet?.backedUp && (
        <>
          <div className="section-title" style={{ marginTop: 16 }}>Username</div>
          <div style={{ margin: '0 16px 8px', background: '#110E30', borderRadius: 20, border: '1px solid rgba(255,255,255,0.07)', padding: '16px' }}>
            {wallet?.username ? (
              <div style={{ marginBottom: 14, padding: '10px 14px', background: 'rgba(124,92,246,0.08)', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>@</span>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 800, color: '#A78BFA' }}>@{wallet.username}</div>
                  <div style={{ fontSize: '11px', color: '#4B5170', marginTop: 2 }}>Your current username — others can send to this handle</div>
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: 14, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 12 }}>
                <div style={{ fontSize: '13px', color: '#EF4444', fontWeight: 600, marginBottom: 2 }}>No username set</div>
                <div style={{ fontSize: '12px', color: '#8B8FA8', lineHeight: 1.5 }}>Claim a username so others can send you funds for free using @yourhandle.</div>
              </div>
            )}

            {/* Lockout notice */}
            {wallet?.usernameSetAt && (() => {
              const setAt = new Date(wallet.usernameSetAt);
              const unlockAt = new Date(setAt.getTime() + 30 * 24 * 60 * 60 * 1000);
              const locked = unlockAt > new Date();
              if (!locked) return null;
              const daysLeft = Math.ceil((unlockAt - new Date()) / (1000 * 60 * 60 * 24));
              return (
                <div style={{ marginBottom: 14, padding: '9px 12px', background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 12 }}>
                  <span style={{ fontSize: '12px', color: '#F59E0B', lineHeight: 1.5 }}>
                    Username can be changed again in <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong>. This 30-day lockout prevents username squatting.
                  </span>
                </div>
              );
            })()}

            {/* Change field — hidden during lockout unless no username yet */}
            {(!wallet?.username || !wallet?.usernameSetAt || new Date(wallet.usernameSetAt).getTime() + 30 * 24 * 60 * 60 * 1000 < Date.now()) && (
              <div>
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#4B5170', fontSize: 16, fontWeight: 700 }}>@</span>
                  <input
                    value={uName}
                    onChange={e => { setUName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 20)); setUNameSuccess(false); }}
                    placeholder={wallet?.username || 'yourhandle'}
                    style={{ width: '100%', height: 46, paddingLeft: 32, paddingRight: 44, borderRadius: 12, border: `1px solid ${uNameCheck === 'available' ? '#10B981' : uNameCheck === 'taken' || uNameCheck === 'invalid' ? '#EF4444' : 'rgba(255,255,255,0.1)'}`, background: '#0D0A29', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                  />
                  {uNameCheck === 'checking' && <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#8B8FA8' }}>…</span>}
                  {uNameCheck === 'available' && <HiCheckCircle size={18} color="#10B981" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }} />}
                  {(uNameCheck === 'taken' || uNameCheck === 'invalid') && <HiXCircle size={18} color="#EF4444" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }} />}
                </div>
                <div style={{ fontSize: '11px', color: uNameCheck === 'available' ? '#10B981' : uNameCheck === 'taken' ? '#EF4444' : uNameCheck === 'invalid' ? '#EF4444' : '#4B5170', marginBottom: 12, minHeight: 16 }}>
                  {uNameCheck === 'available' && '✓ Available'}
                  {uNameCheck === 'taken' && '✗ Already taken'}
                  {uNameCheck === 'invalid' && '✗ 3–20 chars, letters, numbers, underscore only'}
                  {!uNameCheck && '3–20 characters, letters, numbers and underscore only'}
                </div>
                <button
                  onClick={saveUsername}
                  disabled={uNameCheck !== 'available' || uNameSaving || uNameSuccess}
                  style={{ width: '100%', height: 44, borderRadius: 12, border: 'none', background: uNameCheck === 'available' && !uNameSaving && !uNameSuccess ? 'linear-gradient(135deg,#7C5CF6,#5B3FD9)' : 'rgba(124,92,246,0.2)', color: '#fff', fontSize: '14px', fontWeight: 700, cursor: uNameCheck === 'available' ? 'pointer' : 'default' }}
                >
                  {uNameSaving ? 'Saving…' : uNameSuccess ? '✓ Saved' : wallet?.username ? 'Change Username' : 'Claim Username'}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Payment Accounts */}
      {wallet?.backedUp && (
        <>
          <div className="section-title" style={{ marginTop: 16 }}>Payment Accounts</div>
          <div style={{ margin: '0 16px 8px', background: '#110E30', borderRadius: 20, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden' }}>
            {payAccounts.length === 0 ? (
              <div style={{ padding: '12px 16px', color: '#4B5170', fontSize: '13px' }}>No accounts added yet.</div>
            ) : payAccounts.map((acc, i) => (
              <React.Fragment key={acc._id}>
                {i > 0 && <div className="divider" style={{ margin: '0 16px' }} />}
                <PayAccountRow acc={acc} onDelete={() => setDeleteAcc(acc)} />
              </React.Fragment>
            ))}
            {payAccounts.length > 0 && <div className="divider" style={{ margin: '0 16px' }} />}
            <ProfileRow
              icon={<HiPlus size={20} style={{ color: '#7C5CF6' }} />}
              iconBg="rgba(124,92,246,0.12)"
              label="Add Payment Account"
              sub="Bank transfer or mobile money for P2P trades"
              onClick={() => setPayAccOpen(true)}
            />
          </div>
        </>
      )}

      {/* Legal */}
      <div className="section-title" style={{ marginTop: 16 }}>Legal</div>
      <div style={{ margin: '0 16px 16px', background: '#110E30', borderRadius: 20, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden' }}>
        <ProfileRow
          icon={<HiDocumentText size={20} style={{ color: '#8B8FA8' }} />}
          label="Terms of Service"
          onClick={() => navigate('/legal?tab=terms')}
        />
        <div className="divider" />
        <ProfileRow
          icon={<HiDocumentText size={20} style={{ color: '#8B8FA8' }} />}
          label="Privacy Policy"
          onClick={() => navigate('/legal?tab=privacy')}
        />
      </div>

      {/* Danger */}
      <div className="section-title" style={{ marginTop: 4 }}>Account</div>
      <div style={{ margin: '0 16px 32px', background: '#110E30', borderRadius: 20, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden' }}>
        <ProfileRow
          icon={<HiArrowRightOnRectangle size={20} style={{ color: '#EF4444' }} />}
          iconBg="rgba(239,68,68,0.1)"
          label={needsBackup ? 'Delete Wallet' : 'Sign Out'}
          sub={needsBackup ? 'Permanently removes wallet from this browser' : 'You can recover with email & password'}
          onClick={() => setSignOutOpen(true)}
          danger
        />
      </div>

      {/* Backup modal */}
      <BackupModal open={backupOpen} onClose={() => setBackupOpen(false)} />

      {/* KYC modal */}
      <KYCModal open={kycOpen} onClose={() => setKycOpen(false)} kycStatus={kycStatus} />

      {/* Change password modal */}
      <ChangePasswordModal open={changePwOpen} onClose={() => setChangePwOpen(false)} />

      {/* Sign out confirm */}
      <Modal open={signOutOpen} onClose={() => setSignOutOpen(false)} title={needsBackup ? 'Delete Wallet?' : 'Sign Out?'}>
        <p style={{ color: '#8B8FA8', fontSize: '14px', lineHeight: 1.6, marginTop: 0 }}>
          {needsBackup
            ? 'This will permanently remove your wallet from this browser. You will lose access to any funds unless you have the private key saved elsewhere.'
            : 'You can recover your wallet at any time using your email and backup password.'}
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" size="md" full onClick={() => setSignOutOpen(false)}>Cancel</Button>
          <Button variant="danger"    size="md" full onClick={() => { signOutWallet(); setSignOutOpen(false); }}>
            {needsBackup ? 'Delete' : 'Sign Out'}
          </Button>
        </div>
      </Modal>

      {/* Phone number */}
      <PhoneModal open={phoneOpen} onClose={() => setPhoneOpen(false)} walletEmail={wallet?.email} currentPhone={wallet?.phone} />

      {/* Add payment account */}
      <PaymentAccountModal
        open={payAccOpen}
        onClose={() => setPayAccOpen(false)}
        walletEmail={wallet?.email}
        kycStatus={wallet?.kycStatus}
        onAdded={acc => setPayAccounts(prev => [...prev, acc])}
      />

      {/* Delete payment account confirm */}
      <Modal open={!!deleteAcc} onClose={() => !deleteLoading && setDeleteAcc(null)} title="Remove Account?">
        {deleteAcc && (
          <>
            <p style={{ color: '#8B8FA8', fontSize: '14px', lineHeight: 1.6, marginTop: 0 }}>
              Remove <strong style={{ color: '#fff' }}>{deleteAcc.provider}</strong> ···{deleteAcc.accountNumber.slice(-4)}?
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="secondary" size="md" full onClick={() => setDeleteAcc(null)} disabled={deleteLoading}>Cancel</Button>
              <Button variant="danger" size="md" full loading={deleteLoading} onClick={async () => {
                setDeleteLoading(true);
                try {
                  await apiClient.delete(`/payment-accounts/${deleteAcc._id}`, { data: { email: wallet?.email } });
                  setPayAccounts(prev => prev.filter(a => a._id !== deleteAcc._id));
                  setDeleteAcc(null);
                  toast.success('Account removed');
                } catch (e) {
                  toast.error(e.response?.data?.error || 'Failed to remove account');
                } finally {
                  setDeleteLoading(false);
                }
              }}>Remove</Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

/* ─── Profile row ────────────────────────────────────────── */
function ProfileRow({ icon, iconBg, label, sub, onClick, danger }) {
  const inner = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', width: '100%' }}>
      <div style={{ width: 40, height: 40, borderRadius: 12, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1, textAlign: 'left' }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: danger ? '#EF4444' : '#fff' }}>{label}</div>
        {sub && <div style={{ fontSize: '12px', color: '#4B5170', marginTop: 2 }}>{sub}</div>}
      </div>
      {onClick && <HiChevronRight size={16} style={{ color: '#4B5170', flexShrink: 0 }} />}
    </div>
  );

  return onClick
    ? <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%' }}>{inner}</button>
    : <div>{inner}</div>;
}

/* ─── Backup modal ───────────────────────────────────────── */
function BackupModal({ open, onClose }) {
  const { backupWallet } = useContext(WalletContext);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [done, setDone]         = useState(false);

  const handle = async () => {
    if (!email)                     { setError('Email required'); return; }
    if (!isPasswordValid(password)) { setError('Password does not meet all requirements'); return; }
    if (password !== confirm)       { setError('Passwords do not match'); return; }
    setLoading(true); setError('');
    try {
      await backupWallet(email, password, displayName.trim());
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Account creation failed');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <Modal open={open} onClose={onClose} title="Email sent!">
        <div style={{ textAlign: 'center', padding: '8px 0 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <HiEnvelope size={40} style={{ color: '#7C5CF6' }} />
          <p style={{ color: '#8B8FA8', fontSize: '14px', lineHeight: 1.6, margin: 0 }}>
            A verification link was sent to <strong style={{ color: '#fff' }}>{email}</strong>. Click it to verify your address.
          </p>
          <Button variant="primary" size="md" full onClick={onClose}>Done</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Create Your Account">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ color: '#8B8FA8', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
          Your seed phrase already secures your funds. Creating an account lets you access your wallet from any device with just your email and password.
        </p>
        <Input label="Display Name" placeholder="e.g. James K." value={displayName} onChange={e => setDisplayName(e.target.value.slice(0, 20))} />
        <p style={{ color: '#4B5170', fontSize: '11px', margin: '-8px 0 0', paddingLeft: 2 }}>Shown to other traders. 3–20 characters. Cannot be changed after set.</p>
        <Input label="Email" type="email" placeholder="you@email.com" value={email} onChange={e => setEmail(e.target.value)} />
        <PasswordInput label="Password" value={password} onChange={e => setPassword(e.target.value)} />
        <Input label="Confirm Password" type="password" placeholder="Re-enter" value={confirm} onChange={e => setConfirm(e.target.value)} />
        {error && <p style={{ color: '#EF4444', fontSize: '13px', margin: 0 }}>{error}</p>}
        <Button variant="primary" size="lg" full onClick={handle} loading={loading}>Create Account</Button>
      </div>
    </Modal>
  );
}

/* ─── Change password modal ──────────────────────────────── */
function ChangePasswordModal({ open, onClose }) {
  const { changePassword } = useContext(WalletContext);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw]         = useState('');
  const [confirm, setConfirm]     = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  const reset = () => { setCurrentPw(''); setNewPw(''); setConfirm(''); setError(''); };

  const handle = async () => {
    if (!currentPw)                { setError('Enter your current password'); return; }
    if (!isPasswordValid(newPw))   { setError('New password does not meet requirements'); return; }
    if (newPw !== confirm)         { setError('Passwords do not match'); return; }
    if (newPw === currentPw)       { setError('New password must be different from current'); return; }
    setLoading(true); setError('');
    try {
      await changePassword(currentPw, newPw);
      reset();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Password change failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} title="Change Password">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ color: '#8B8FA8', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
          Your wallet is re-encrypted in your browser with the new password. The server never sees your recovery phrase.
        </p>
        <PasswordInput label="Current Password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} />
        <PasswordInput label="New Password"     value={newPw}     onChange={e => setNewPw(e.target.value)} />
        <Input label="Confirm New Password" type="password" placeholder="Re-enter" value={confirm} onChange={e => setConfirm(e.target.value)} />
        {error && <p style={{ color: '#EF4444', fontSize: '13px', margin: 0 }}>{error}</p>}
        <Button variant="primary" size="lg" full onClick={handle} loading={loading}>Update Password</Button>
      </div>
    </Modal>
  );
}

/* ─── KYC modal ──────────────────────────────────────────── */
function KYCModal({ open, onClose, kycStatus }) {
  const { wallet, setWallet }  = useContext(WalletContext);
  const fileRefs    = { idFront: useRef(), idBack: useRef(), selfie: useRef(), liveness: useRef(), utilityBill: useRef() };
  const [files, setFiles]       = useState({});
  const [loading, setLoading]   = useState(false);
  const maxDob = (() => {
    const d = new Date();
    const y = d.getFullYear() - 18;
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  })();

  const [step, setStep]         = useState('info');
  const [firstName, setFirstName]   = useState('');
  const [lastName, setLastName]     = useState('');
  const [otherNames, setOtherNames] = useState('');
  const [dob, setDob]               = useState('');
  const [idType, setIdType]         = useState('');
  const [idNumber, setIdNumber]     = useState('');
  const [countryIso, setCountryIso] = useState('');
  const [stateIso, setStateIso]     = useState('');
  const [city, setCity]             = useState('');
  const [street, setStreet]         = useState('');
  const [kycPhone, setKycPhone]     = useState('');
  const [utilityBillDate, setUtilityBillDate] = useState('');

  const allCountries = useMemo(() => Country.getAllCountries(), []);
  const statesForCountry = useMemo(() => countryIso ? State.getStatesOfCountry(countryIso) : [], [countryIso]);
  const [infoError, setInfoError]   = useState('');
  /* Payment account — optional step 3 */
  const [paType, setPaType]               = useState('bank_transfer');
  const [paProvider, setPaProvider]       = useState('');
  const [paAccountNumber, setPaAccountNumber] = useState('');
  const [paCurrency, setPaCurrency]       = useState('');

  const setFile = (key, file) => setFiles(prev => ({ ...prev, [key]: file }));

  const ALL_DOCS = [
    { key: 'idFront',     label: 'ID Front',        sub: 'Front side of passport / national ID / license' },
    { key: 'idBack',      label: 'ID Back',          sub: 'Back side of your ID document' },
    { key: 'selfie',      label: 'Selfie + ID',      sub: 'Hold your ID next to your face, clearly visible' },
    { key: 'liveness',    label: 'Liveness Check',   sub: "Hold a handwritten note with your email and today's date next to your face" },
    { key: 'utilityBill', label: 'Utility Bill',     sub: 'Water, electricity, waste or internet bill — dated within the last 3 months, showing your name and address' },
  ];

  const requiredDocs = ALL_DOCS;

  const allReady = requiredDocs.every(d => !!files[d.key]);

  const handleInfoNext = () => {
    if (!firstName.trim()) { setInfoError('First name is required'); return; }
    if (!lastName.trim())  { setInfoError('Last name is required'); return; }
    if (!dob.trim())       { setInfoError('Date of birth is required'); return; }
    if (dob > maxDob)      { setInfoError('You must be at least 18 years old to use Ikhua'); return; }
    if (!countryIso)       { setInfoError('Country is required'); return; }
    if (statesForCountry.length > 0 && !stateIso) { setInfoError('State / Province is required'); return; }
    if (!city.trim())      { setInfoError('City is required'); return; }
    if (!street.trim())    { setInfoError('Street address is required'); return; }
    if (!kycPhone.trim())  { setInfoError('WhatsApp / phone number is required'); return; }
    if (!idType)           { setInfoError('Please select your document type'); return; }
    if (!idNumber.trim())  { setInfoError('Document number is required'); return; }
    if (!utilityBillDate)  { setInfoError('Utility bill date is required'); return; }
    const billDate = new Date(utilityBillDate);
    const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
    if (billDate > new Date())      { setInfoError('Utility bill date cannot be in the future'); return; }
    if (billDate < ninetyDaysAgo)   { setInfoError('Utility bill must be dated within the last 3 months'); return; }
    setInfoError('');
    setStep('photos');
  };

  const handleSubmit = async (includePayment = false) => {
    if (!allReady) { toast.error(`Please upload: ${requiredDocs.filter(d => !files[d.key]).map(d => d.label).join(', ')}`); return; }
    setLoading(true);
    try {
      // Compress images client-side before upload to reduce bandwidth + Cloudinary storage
      const compressImage = (file) => new Promise((resolve) => {
        if (!file.type.startsWith('image/')) { resolve(file); return; }
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          const MAX_PX = 1600; // higher than chat — ID text must stay readable
          const scale  = Math.min(1, MAX_PX / Math.max(img.width, img.height));
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          canvas.toBlob(blob => resolve(blob ?? file), 'image/jpeg', 0.85);
        };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
        img.src = url;
      });

      const compressed = await Promise.all(
        Object.entries(files).map(async ([k, f]) => [k, await compressImage(f)])
      );

      const form = new FormData();
      form.append('email',      wallet?.email);
      form.append('firstName',  firstName.trim());
      form.append('lastName',   lastName.trim());
      form.append('otherNames', otherNames.trim());
      form.append('dob',     dob.trim());
      const selectedCountry = allCountries.find(c => c.isoCode === countryIso);
      const selectedState   = statesForCountry.find(s => s.isoCode === stateIso);
      form.append('country', selectedCountry?.name || countryIso);
      form.append('state',   selectedState?.name  || '');
      form.append('city',    city.trim());
      form.append('street',   street.trim());
      form.append('idType',          idType);
      form.append('idNumber',        idNumber.trim());
      form.append('phone',           kycPhone.trim());
      form.append('utilityBillDate', utilityBillDate.trim());
      compressed.forEach(([k, f]) => form.append(k, f, 'photo.jpg'));

      const promises = [
        apiClient.post('/kyc/submit', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
      ];
      if (includePayment && paProvider.trim() && paAccountNumber.trim()) {
        promises.push(apiClient.post('/payment-accounts/add', {
          email: wallet?.email, type: paType,
          provider: paProvider.trim(), accountNumber: paAccountNumber.trim(),
          currency: paCurrency.trim().toUpperCase(),
        }));
      }

      const [kycResult, payResult] = await Promise.allSettled(promises);

      if (kycResult.status === 'rejected') {
        toast.error(kycResult.reason?.response?.data?.error || 'Submission failed. Please try again.');
        return;
      }
      toast.success("KYC submitted! We'll review within 24–48 hours.");
      if (payResult?.status === 'rejected') {
        toast.warn('Payment account could not be saved — you can add it from Profile later.');
      }
      setWallet({ ...wallet, kycStatus: 'pending', kycRejectionReason: null, phone: kycResult.value?.data?.phone || kycPhone.trim() });
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (kycStatus === 'pending') {
    return (
      <Modal open={open} onClose={onClose} title="Verification Pending">
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <HiClock size={48} style={{ color: '#F59E0B', margin: '0 auto 12px', display: 'block' }} />
          <p style={{ color: '#8B8FA8', fontSize: '14px', lineHeight: 1.6 }}>
            Your documents are under review. We typically respond within 24–48 hours.
          </p>
        </div>
      </Modal>
    );
  }

  /* Step 1 — personal info */
  if (step === 'info') {
    return (
      <Modal open={open} onClose={onClose} title="Identity Verification">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ color: '#8B8FA8', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
            Enter your details exactly as they appear on your ID.
          </p>
          <Input label="First name — exactly as on your ID" placeholder="e.g. Sarah" value={firstName}  onChange={e => setFirstName(e.target.value)} />
          <Input label="Last name — exactly as on your ID"  placeholder="e.g. Johnson" value={lastName}   onChange={e => setLastName(e.target.value)} />
          <Input label="Other / middle name (if on your ID)" placeholder="Optional — only if it appears on your ID" value={otherNames} onChange={e => setOtherNames(e.target.value)} />
          <Input label="Date of birth" type="date" value={dob} max={maxDob} onChange={e => setDob(e.target.value)} hint="You must be at least 18 years old" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#8B8FA8', paddingLeft: 2 }}>Document type</label>
            <select className="input" value={idType} onChange={e => setIdType(e.target.value)}>
              <option value="">Select document type…</option>
              <option value="passport">Passport</option>
              <option value="national_id">National ID Card</option>
              <option value="drivers_license">Driver's License</option>
              <option value="voter_id">Voter ID / Voter Card</option>
              <option value="residence_permit">Residence Permit</option>
              <option value="other">Other</option>
            </select>
          </div>
          <Input
            label="Document number — exactly as it appears on your ID"
            placeholder="e.g. A12345678"
            value={idNumber}
            onChange={e => setIdNumber(e.target.value)}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#8B8FA8', paddingLeft: 2 }}>Country of residence</label>
            <select className="input" value={countryIso} onChange={e => { setCountryIso(e.target.value); setStateIso(''); }}>
              <option value="">Select country…</option>
              {allCountries.map(c => <option key={c.isoCode} value={c.isoCode}>{c.flag} {c.name}</option>)}
            </select>
          </div>
          {statesForCountry.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#8B8FA8', paddingLeft: 2 }}>State / Province</label>
              <select className="input" value={stateIso} onChange={e => setStateIso(e.target.value)}>
                <option value="">Select state…</option>
                {statesForCountry.map(s => <option key={s.isoCode} value={s.isoCode}>{s.name}</option>)}
              </select>
            </div>
          )}
          <Input label="City" placeholder="Your city" value={city} onChange={e => setCity(e.target.value)} />
          <Input label="Street address" placeholder="House number and street name" value={street} onChange={e => setStreet(e.target.value)} />
          <Input
            label="WhatsApp / phone number"
            placeholder="+1 555 000 1234 (include country code)"
            hint="Shared with trade partners so they can contact you directly"
            value={kycPhone}
            onChange={e => setKycPhone(e.target.value)}
          />
          <div style={{ background: 'rgba(124,92,246,0.06)', border: '1px solid rgba(124,92,246,0.15)', borderRadius: 12, padding: '12px 14px' }}>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#7C5CF6', margin: '0 0 4px' }}>Proof of Address</p>
            <p style={{ fontSize: '12px', color: '#8B8FA8', margin: 0, lineHeight: 1.5 }}>
              You will upload a utility bill (electricity, water, internet, waste) in the next step. Enter the date printed on the bill here — it must be within the last 3 months.
            </p>
          </div>
          <Input
            label="Date printed on your utility bill"
            type="date"
            value={utilityBillDate}
            max={new Date().toISOString().split('T')[0]}
            min={new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
            onChange={e => setUtilityBillDate(e.target.value)}
            hint="Must be within the last 3 months — older bills will be rejected"
          />
          {infoError && <p style={{ color: '#EF4444', fontSize: '13px', margin: 0 }}>{infoError}</p>}
          <Button variant="primary" size="lg" full onClick={handleInfoNext}>Continue to Photos</Button>
        </div>
      </Modal>
    );
  }

  /* Step 3 — payment account (optional) */
  if (step === 'payment') {
    const kycName = [firstName.trim(), otherNames.trim(), lastName.trim()].filter(Boolean).join(' ');
    const paReady = !!(paProvider.trim() && paAccountNumber.trim() && paCurrency);
    return (
      <Modal open={open} onClose={onClose} title="Payment Account (Optional)">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ color: '#8B8FA8', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
            Add a bank or mobile money account for P2P trades. You can skip and add accounts from your profile later.
          </p>
          <div style={{ padding: '10px 12px', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 10 }}>
            <p style={{ fontSize: '12px', color: '#10B981', margin: 0 }}>
              Account name is automatically set to your verified name: <strong>{kycName}</strong>
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[['bank_transfer', 'Bank Transfer'], ['mobile_money', 'Mobile Money']].map(([v, l]) => (
              <button key={v} onClick={() => setPaType(v)}
                style={{ flex: 1, padding: '10px', borderRadius: 12, border: `1px solid ${paType === v ? '#7C5CF6' : 'rgba(255,255,255,0.07)'}`, background: paType === v ? 'rgba(124,92,246,0.12)' : '#1A1748', color: paType === v ? '#7C5CF6' : '#8B8FA8', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
              >{l}</button>
            ))}
          </div>
          <Input
            label={paType === 'bank_transfer' ? 'Bank name' : 'Provider (e.g. MTN MoMo)'}
            placeholder={paType === 'bank_transfer' ? 'e.g. Chase, Barclays' : 'e.g. PayPal, Wise'}
            value={paProvider} onChange={e => setPaProvider(e.target.value)}
          />
          <Input
            label="Account number"
            placeholder={paType === 'bank_transfer' ? 'Account number' : 'Phone number'}
            value={paAccountNumber} onChange={e => setPaAccountNumber(e.target.value)}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: '13px', color: '#8B8FA8', fontWeight: 500 }}>Currency</label>
            <select className="input" value={paCurrency} onChange={e => setPaCurrency(e.target.value)}>
              <option value="">Select currency…</option>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="secondary" size="md" full onClick={() => setStep('photos')}>Back</Button>
            <Button variant="primary"   size="md" full onClick={() => handleSubmit(true)} loading={loading} disabled={!paReady}>Add & Submit</Button>
          </div>
          <button
            onClick={() => handleSubmit(false)} disabled={loading}
            style={{ background: 'none', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', color: '#4B5170', fontSize: '13px', padding: '4px 0', textDecoration: 'underline' }}
          >
            {loading ? 'Uploading photos — please wait…' : 'Skip — submit KYC without payment account'}
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Upload Documents">
      <p style={{ color: '#8B8FA8', fontSize: '13px', lineHeight: 1.6, marginTop: 0, marginBottom: 16 }}>
        Upload all 4 photos clearly. Our team reviews within 24–48 hours.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {requiredDocs.map(doc => (
          <div key={doc.key}>
            <input
              type="file" accept="image/*" capture="environment"
              ref={fileRefs[doc.key]}
              style={{ display: 'none' }}
              onChange={e => setFile(doc.key, e.target.files[0])}
            />
            <button
              onClick={() => fileRefs[doc.key].current.click()}
              style={{
                width: '100%', padding: '12px 14px', borderRadius: 14,
                border: `1px solid ${files[doc.key] ? 'rgba(16,185,129,0.4)' : 'rgba(255,255,255,0.07)'}`,
                background: files[doc.key] ? 'rgba(16,185,129,0.06)' : '#1A1748',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
              }}
            >
              <div style={{ width: 36, height: 36, borderRadius: 10, background: files[doc.key] ? 'rgba(16,185,129,0.12)' : 'rgba(124,92,246,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {files[doc.key]
                  ? <HiCheckCircle size={20} style={{ color: '#10B981' }} />
                  : <HiCamera size={20} style={{ color: '#7C5CF6' }} />}
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>{doc.label}</div>
                <div style={{ fontSize: '11px', color: '#4B5170', lineHeight: 1.3, marginTop: 2 }}>{doc.sub}</div>
              </div>
            </button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <Button variant="secondary" size="md" full onClick={() => setStep('info')}>Back</Button>
        <Button variant="primary"   size="md" full onClick={() => { if (allReady) setStep('payment'); }} loading={loading} disabled={!allReady}>Continue</Button>
      </div>
    </Modal>
  );
}

/* ─── Payment account row ───────────────────────────────── */
function PayAccountRow({ acc, onDelete }) {
  const isBank = acc.type === 'bank_transfer';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px' }}>
      <div style={{ width: 40, height: 40, borderRadius: 12, background: isBank ? 'rgba(98,116,241,0.12)' : 'rgba(16,185,129,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {isBank
          ? <HiBuildingLibrary size={20} style={{ color: '#6274F1' }} />
          : <HiDevicePhoneMobile size={20} style={{ color: '#10B981' }} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>{acc.provider}</div>
        <div style={{ fontSize: '12px', color: '#8B8FA8', marginTop: 2 }}>{acc.accountNumber} · {acc.accountName}</div>
        <div style={{ fontSize: '11px', color: '#4B5170', marginTop: 1 }}>{acc.currency} · {isBank ? 'Bank Transfer' : 'Mobile Money'}</div>
      </div>
      <button
        onClick={onDelete}
        style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 10, padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
      >
        <HiTrash size={16} style={{ color: '#EF4444' }} />
      </button>
    </div>
  );
}

/* ─── Standalone add-payment-account modal ───────────────── */
function PaymentAccountModal({ open, onClose, walletEmail, kycStatus, onAdded }) {
  const [type, setType]                   = useState('bank_transfer');
  const [provider, setProvider]           = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName]     = useState('');
  const [currency, setCurrency]           = useState('');
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState('');

  const isKycd = kycStatus === 'approved';
  const reset  = () => { setType('bank_transfer'); setProvider(''); setAccountNumber(''); setAccountName(''); setCurrency(''); setError(''); };

  const handleAdd = async () => {
    if (!provider.trim())                      { setError('Provider / bank name is required'); return; }
    if (!accountNumber.trim())                 { setError('Account number is required'); return; }
    if (!isKycd && !accountName.trim())        { setError('Account holder name is required'); return; }
    if (!currency)                             { setError('Please select a currency'); return; }
    setLoading(true); setError('');
    try {
      const res = await apiClient.post('/payment-accounts/add', {
        email: walletEmail, type,
        provider: provider.trim(),
        accountNumber: accountNumber.trim(),
        accountName: accountName.trim() || undefined,
        currency,
      });
      onAdded(res.data.account);
      reset();
      onClose();
      toast.success('Payment account added');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to add account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} title="Add Payment Account">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ padding: '10px 12px', background: isKycd ? 'rgba(16,185,129,0.06)' : 'rgba(245,158,11,0.06)', border: `1px solid ${isKycd ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)'}`, borderRadius: 10 }}>
          <p style={{ fontSize: '12px', color: isKycd ? '#10B981' : '#F59E0B', margin: 0, lineHeight: 1.5 }}>
            {isKycd
              ? 'Your account name is automatically set to your KYC verified name. Buyers will see this name and must verify it matches before paying.'
              : 'Enter the name on your bank account exactly as it appears. Complete KYC to have your name verified for extra buyer trust.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[['bank_transfer', 'Bank Transfer'], ['mobile_money', 'Mobile Money']].map(([v, l]) => (
            <button key={v} onClick={() => setType(v)}
              style={{ flex: 1, padding: '10px', borderRadius: 12, border: `1px solid ${type === v ? '#7C5CF6' : 'rgba(255,255,255,0.07)'}`, background: type === v ? 'rgba(124,92,246,0.12)' : '#1A1748', color: type === v ? '#7C5CF6' : '#8B8FA8', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
            >{l}</button>
          ))}
        </div>
        <Input
          label={type === 'bank_transfer' ? 'Bank name' : 'Provider (e.g. MTN MoMo)'}
          placeholder={type === 'bank_transfer' ? 'e.g. Chase, Barclays' : 'e.g. PayPal, Wise'}
          value={provider} onChange={e => setProvider(e.target.value)}
        />
        <Input
          label="Account number"
          placeholder={type === 'bank_transfer' ? 'Account number' : 'Phone number'}
          value={accountNumber} onChange={e => setAccountNumber(e.target.value)}
        />
        {!isKycd && (
          <Input
            label="Account holder name"
            placeholder="Full name exactly as on your bank account"
            value={accountName} onChange={e => setAccountName(e.target.value)}
          />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: '13px', color: '#8B8FA8', fontWeight: 500 }}>Currency</label>
          <select className="input" value={currency} onChange={e => setCurrency(e.target.value)}>
            <option value="">Select currency…</option>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {error && <p style={{ color: '#EF4444', fontSize: '13px', margin: 0 }}>{error}</p>}
        <Button variant="primary" size="lg" full onClick={handleAdd} loading={loading}>Add Account</Button>
      </div>
    </Modal>
  );
}

/* ─── Phone number modal ─────────────────────────────────── */
function PhoneModal({ open, onClose, walletEmail, currentPhone }) {
  const { wallet, setWallet } = useContext(WalletContext);
  const [phone, setPhone]     = useState(currentPhone || '');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [otpOpen, setOtpOpen] = useState(false);

  useEffect(() => { setPhone(currentPhone || ''); }, [currentPhone]);

  const handleSave = () => {
    if (!phone.trim()) { setError('Phone number is required'); return; }
    setError('');
    setOtpOpen(true);
  };

  const doSave = async (otpCode) => {
    setLoading(true);
    try {
      const { data } = await apiClient.patch('/wallet/phone', { email: walletEmail, phone: phone.trim(), otpCode });
      setWallet({ ...wallet, phone: data.phone });
      toast.success(data.phone ? 'Phone number updated' : 'Phone number removed');
      setOtpOpen(false);
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save phone number');
      setOtpOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose} title="WhatsApp / Phone Number">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ color: '#8B8FA8', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
            Shared with your trade partner when a trade is active so you can resolve issues directly on WhatsApp before raising a dispute.
          </p>
          <Input
            label="WhatsApp number (with country code)"
            type="tel"
            placeholder="e.g. +1 234 567 8900"
            value={phone}
            onChange={e => setPhone(e.target.value)}
          />
          {error && <p style={{ color: '#EF4444', fontSize: '13px', margin: 0 }}>{error}</p>}
          <Button variant="primary" size="lg" full onClick={handleSave} loading={loading}>Save</Button>
        </div>
      </Modal>
      <OtpModal
        open={otpOpen}
        onClose={() => setOtpOpen(false)}
        email={walletEmail}
        action="phone_reset"
        onConfirm={doSave}
      />
    </>
  );
}
